import { Node } from "@tiptap/core";
import {
  checkAndHandleMatrixCellCitation,
  citationAnchorValue,
} from "../common/MarkDownRenderer";

const NumericCitationNode = Node.create({
  name: "numeric-citation",
  group: "inline",
  content: "text*",
  inline: true,

  addAttributes() {
    return {
      cids: {
        default: "",
        parseHTML: (element) => element.getAttribute("cids") || "",
        renderHTML: (attrs) => ({ cids: attrs.cids }),
      },
      qid: {
        default: "",
        parseHTML: (element) => element.getAttribute("qid") || "",
        renderHTML: (attrs) => ({ qid: attrs.qid }),
      },
      match: {
        default: "",
        parseHTML: (element) => element.getAttribute("match") || "",
        renderHTML: (attrs) => ({ match: attrs.match }),
      },
      originalNumValue: {
        default: "",
        parseHTML: (element) => element.textContent || "",
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "numeric-citation" }];
  },

  renderHTML({ HTMLAttributes }) {
    // Strip everything we don't want in the DOM.
    const { originalNumValue, dataTextContent, originalnumvalue, ...attrs } =
      HTMLAttributes as Record<string, any>;
    // Use 0 placeholder so TipTap preserves the existing child text.
    return ["numeric-citation", attrs, 0];
  },

  addNodeView() {
    return ({ node }) => {
      const { cids, qid, match } = node.attrs;
      const dom = document.createElement("span");
      // The matrix-cell lookup still keys off the raw `match` literal, which
      // is what it has always matched on.
      const highlightValue = match || node.textContent;
      // The cell anchor does NOT: `match` is every figure on the answer's
      // row, pipe-separated, and preferring it over the node's own figure is
      // exactly the wrong-cell selection #11454 removed from the markdown
      // renderer. This node is the same citation in the reports editor and
      // the matrix viewer, so it needs the same arbitration.
      const anchorValue = citationAnchorValue(node.textContent, match);

      dom.textContent = node.textContent;
      dom.className = "text-blue-600 cursor-pointer";

      dom.onclick = async (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const idList = (cids || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        // Check if this citation references a matrix cell (for matrix workspaces)
        // For multiple citations, check the first one
        if (qid && idList.length > 0) {
          const firstCitationId = idList[0];
          const isMatrixCellCitation = await checkAndHandleMatrixCellCitation(
            firstCitationId,
            qid,
            highlightValue
          );
          if (isMatrixCellCitation) {
            // Matrix cell citation was handled, don't open document panel
            return;
          }
        }

        // Dispatch the openDocumentPanel event for consistency
        if (idList.length > 1) {
          window.dispatchEvent(
            new CustomEvent("openDocumentPanel", {
              detail: {
                citationId: idList.join(","),
                queryId: qid,
                originalNumValue: anchorValue,
                multipleCitations: idList.join(","),
              },
            })
          );
        } else if (idList.length === 1) {
          window.dispatchEvent(
            new CustomEvent("openDocumentPanel", {
              detail: {
                citationId: idList[0],
                queryId: qid,
                originalNumValue: anchorValue,
              },
            })
          );
        }

        // Also call handleReferenceClick if available for backward compatibility
        if ((window as any).handleReferenceClick) {
          if (idList.length > 1) {
            (window as any).handleReferenceClick(e, {
              qid,
              multipleCitations: idList.join(","),
              originalNumValue: anchorValue,
            });
          } else if (idList.length === 1) {
            (window as any).handleReferenceClick(e, {
              qid,
              id: `[${idList[0]}]`,
              originalNumValue: anchorValue,
            });
          }
        }
      };

      return { dom };
    };
  },
});

export default NumericCitationNode;
